import { Component, ErrorInfo, FC, ReactNode } from "react"
import { Alert } from "@mui/material"
import { TgMailing } from "../../interfaces/mailing"
import { TelegramBubble, InlineKeyboardMarkup } from "./TelegramBubble"
import { TelegramTextContent } from "./TelegramTextContent"
import { TelegramMediaContent } from "./TelegramMediaContent"
import { MessageEntity } from "./entityRenderer"
import { TelegramForwardPreview } from "./TelegramForwardPreview"

// ---------------------------------------------------------------------------
// Error Boundary
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  error: Error | null
}

class PreviewErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[TelegramPreview] render error:", error, info)
  }

  render() {
    if (this.state.error) {
      return (
        <Alert severity="error">
          Ошибка рендеринга: {this.state.error.message}
        </Alert>
      )
    }
    return this.props.children
  }
}

// ---------------------------------------------------------------------------
// Helpers: extract text / media from body based on method
// ---------------------------------------------------------------------------

type AnyBody = Partial<TgMailing.ApiMethodBody[TgMailing.AvailableMethods]>
type MediaInfo = { url: string; type: "photo" | "video" | "animation" } | null

function extractText(
  method: TgMailing.AvailableMethods,
  body: AnyBody,
): string {
  const b = body as any
  if (method === "sendMessage") return b?.text ?? ""
  if (
    method === "sendPhoto" ||
    method === "sendVideo" ||
    method === "sendAnimation"
  )
    return b?.caption ?? ""
  return ""
}

function extractParseMode(body: AnyBody): string | null {
  return (body as any)?.parse_mode ?? null
}

function extractEntities(body: AnyBody): MessageEntity[] {
  return (body as any)?.entities ?? []
}

function extractMedia(
  method: TgMailing.AvailableMethods,
  body: AnyBody,
): MediaInfo {
  const b = body as any
  if (method === "sendPhoto" && b?.photo) return { url: b.photo, type: "photo" }
  if (method === "sendVideo" && b?.video) return { url: b.video, type: "video" }
  if (method === "sendAnimation" && b?.animation)
    return { url: b.animation, type: "animation" }
  return null
}

function extractReplyMarkup(body: AnyBody): InlineKeyboardMarkup | undefined {
  let rm = (body as any)?.reply_markup
  // В CSV-рассылке reply_markup приходит из колонки как JSON-строка —
  // пытаемся распарсить её, чтобы предпросмотр показал кнопки.
  if (typeof rm === "string") {
    const trimmed = rm.trim()
    if (!trimmed) return undefined
    try {
      rm = JSON.parse(trimmed)
    } catch {
      return undefined
    }
  }
  if (rm && Array.isArray(rm.inline_keyboard)) return rm as InlineKeyboardMarkup
  return undefined
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface TelegramPreviewProps {
  method: TgMailing.AvailableMethods
  body: AnyBody
  botName?: string
}

/**
 * Renders a Telegram-style message bubble preview for the given method+body.
 * Wraps everything in an ErrorBoundary — render failures show an Alert instead
 * of crashing.
 *
 * Supported methods: sendMessage, sendPhoto, sendVideo, sendAnimation,
 * forwardMessages, copyMessages.
 * For other methods (sendChatAction) a placeholder message is shown.
 */
export const TelegramPreview: FC<TelegramPreviewProps> = ({
  method,
  body,
  botName,
}) => {
  const PREVIEWABLE_METHODS: TgMailing.AvailableMethods[] = [
    "sendMessage",
    "sendPhoto",
    "sendVideo",
    "sendAnimation",
  ]

  if (method === "forwardMessages" || method === "copyMessages") {
    return (
      <PreviewErrorBoundary>
        <TelegramForwardPreview body={body as Record<string, unknown>} />
      </PreviewErrorBoundary>
    )
  }

  if (!PREVIEWABLE_METHODS.includes(method)) {
    return (
      <Alert severity="info">
        Предпросмотр недоступен для метода «{method}»
      </Alert>
    )
  }

  const text = extractText(method, body)
  const parseMode = extractParseMode(body)
  const entities = extractEntities(body)
  const media = extractMedia(method, body)
  const replyMarkup = extractReplyMarkup(body)

  return (
    <PreviewErrorBoundary>
      <TelegramBubble senderName={botName} replyMarkup={replyMarkup}>
        {media && <TelegramMediaContent url={media.url} type={media.type} />}
        <TelegramTextContent
          text={text}
          parseMode={parseMode}
          entities={entities}
        />
      </TelegramBubble>
    </PreviewErrorBoundary>
  )
}
